import between from '@inquisitive/between';
import isString from 'lodash/isString';
import sortBy from 'lodash/sortBy';

import { DIRECTION_LEFT, DIRECTION_RIGHT } from 'webclient.constants';

export const APPEND = 'APPEND';
export const PREPEND = 'PREPEND';

type AnyObject = {
  [key: string]: any;
};

export type MODE =
  | typeof DIRECTION_LEFT
  | typeof DIRECTION_RIGHT
  | typeof APPEND
  | typeof PREPEND;

const findLeftSort = (
  arr: any[],
  sortField: string,
  arrIndex: number,
  rightSort: string
): string => {
  if (arrIndex === 1) return between.lowest;
  const leftSort = arr[arrIndex - 2][sortField];
  if (rightSort === leftSort) {
    return findLeftSort(arr, sortField, arrIndex - 1, rightSort);
  }
  return leftSort;
};

const findRightSort = (
  arr: any[],
  sortField: string,
  arrIndex: number,
  leftSort: string
): string => {
  if (arrIndex + 1 === arr.length - 1) return between.highest;
  const rightSort = arr[arrIndex + 2][sortField];
  if (rightSort === leftSort) {
    return findRightSort(arr, sortField, arrIndex + 1, leftSort);
  }
  return rightSort;
};

export const getSortByDirection = (
  arr: any[],
  arrIndex: number,
  mode: MODE,
  sortField = 'sort'
): string => {
  let leftSort;
  let rightSort;
  if (arr.length === 0 && (mode === APPEND || mode === PREPEND)) {
    leftSort = between.lowest;
    rightSort = between.highest;
  } else if (mode === APPEND) {
    leftSort = arr[arr.length - 1][sortField];
    rightSort = between.highest;
  } else if (mode === PREPEND) {
    leftSort = between.lowest;
    rightSort = arr[0][sortField];
  } else if (mode === DIRECTION_LEFT && arrIndex > 0) {
    rightSort = arr[arrIndex - 1][sortField];
    leftSort = findLeftSort(arr, sortField, arrIndex, rightSort);
  } else if (mode === DIRECTION_RIGHT && arrIndex < arr.length) {
    leftSort = arr[arrIndex + 1][sortField];
    rightSort = findRightSort(arr, sortField, arrIndex, leftSort);
  } else {
    // They're already at one of the ends (left or right) so no point reordering
    return arr[arrIndex][sortField];
  }
  // Calls to 'between' can result in infinte loops if passed invalid arguments
  // So be defensive here
  if (!isString(leftSort) || !isString(rightSort) || leftSort === rightSort) {
    throw Error(`Can't find a string between ${leftSort} and ${rightSort}`);
  }
  return between.between(leftSort, rightSort);
};

export const moveArray = (arr: any[], arrIndex: number, direction: MODE) => {
  const newItem = { ...arr[arrIndex] };
  newItem.sort = getSortByDirection(arr, arrIndex, direction);
  const newArr = [...arr];
  newArr[arrIndex] = newItem;
  return sortBy(newArr, 'sort');
};

export const moveLeft = (arr: AnyObject[], arrIndex: number) =>
  moveArray(arr, arrIndex, DIRECTION_LEFT);

export const moveRight = (arr: AnyObject[], arrIndex: number) =>
  moveArray(arr, arrIndex, DIRECTION_RIGHT);

export const nextSortArray = (arr: AnyObject[], sortField = 'sort') =>
  getSortByDirection(
    sortBy(arr, sortField), // because tests in content.selector.test don't expect sorted state data
    arr.length,
    APPEND,
    sortField
  );

export const prependSortArray = (arr: AnyObject[]) =>
  getSortByDirection(
    sortBy(arr, 'sort'), // because tests in content.selector.test don't expect sorted state data
    arr.length,
    PREPEND
  );

export const nextSortObject = (obj: AnyObject, sortField = 'sort') =>
  nextSortArray(Object.values(obj), sortField);
